import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import type {
  LeadFormDestination,
  LeadFormField,
  LeadFormFieldType,
  UpdateLeadForm,
} from '@tinct/core-api-models';
import { useHubspotReadiness } from './use-hubspot-readiness';

const fieldTypeLabels: Record<LeadFormFieldType, string> = {
  text: 'Text',
  email: 'Email',
  textarea: 'Text area',
  tel: 'Phone',
};

const MAX_FIELDS = 12;

export type LeadFormEditorProps = {
  value: UpdateLeadForm;
  onChange: (value: UpdateLeadForm) => void;
  /** Workspace integrations settings URL — the "Connect HubSpot" link target. */
  integrationsUrl: string;
  /**
   * Once the page is generated the field list is DERIVED from the rendered
   * form markup and re-derived on every version — the page is the source of
   * truth, so the editor shows fields read-only ("edit the form on the page
   * to change fields"). Enabled, destination and success message stay
   * user-owned. Pre-generation (the funnel dialog) leaves this off.
   */
  fieldsReadOnly?: boolean;
  className?: string;
};

/**
 * The lead-form config editor, shared by the funnel's "Add a lead form"
 * dialog and the campaign settings section. Pure controlled component —
 * validation and saving belong to the caller (updateLeadFormSchema).
 */
export function LeadFormEditor({
  value,
  onChange,
  integrationsUrl,
  fieldsReadOnly = false,
  className,
}: LeadFormEditorProps) {
  const { hubspotReady } = useHubspotReadiness();

  const patch = (partial: Partial<UpdateLeadForm>) =>
    onChange({ ...value, ...partial });

  const patchField = (index: number, partial: Partial<LeadFormField>) => {
    const fields = value.fields.map((field, i) =>
      i === index ? { ...field, ...partial } : field,
    );
    patch({ fields });
  };

  const moveField = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= value.fields.length) return;
    const fields = [...value.fields];
    [fields[index], fields[target]] = [fields[target], fields[index]];
    patch({ fields });
  };

  const removeField = (index: number) => {
    patch({ fields: value.fields.filter((_, i) => i !== index) });
  };

  const addField = () => {
    if (value.fields.length >= MAX_FIELDS) return;
    const usedKeys = new Set(value.fields.map((field) => field.key));
    let n = value.fields.length + 1;
    while (usedKeys.has(`field_${n}`)) n += 1;
    patch({
      fields: [
        ...value.fields,
        { key: `field_${n}`, label: '', type: 'text', required: false },
      ],
    });
  };

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {/* Enable toggle */}
      <div className="flex items-center gap-3">
        <Switch
          id="lead-form-enabled"
          checked={value.enabled}
          onCheckedChange={(enabled) => patch({ enabled })}
        />
        <div className="flex flex-col">
          <Label htmlFor="lead-form-enabled">Collect leads with a form</Label>
          <span className="text-xs text-muted-foreground">
            The generator renders an on-brand form on the page.
          </span>
        </div>
      </div>

      {value.enabled && (
        <>
          {/* Destination */}
          <div className="flex flex-col gap-2">
            <Label>Destination</Label>
            <Select
              value={value.destination}
              onValueChange={(destination) =>
                patch({ destination: destination as LeadFormDestination })
              }
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="built_in">Store in Tinct</SelectItem>
                <SelectItem value="hubspot" disabled={!hubspotReady}>
                  Send to HubSpot{hubspotReady ? '' : ' (not connected)'}
                </SelectItem>
              </SelectContent>
            </Select>
            {!hubspotReady && (
              <p className="text-xs text-muted-foreground">
                Submissions can be forwarded as HubSpot contacts —{' '}
                <Link
                  to={integrationsUrl}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  connect HubSpot
                </Link>{' '}
                to enable it.
              </p>
            )}
            {hubspotReady && value.destination === 'hubspot' && (
              <p className="text-xs text-muted-foreground">
                Submissions are stored in Tinct and forwarded as HubSpot
                contacts. If HubSpot was connected before lead forms existed,
                reconnect it once to grant contact-write access — otherwise
                forwarding fails.
              </p>
            )}
          </div>

          {/* Fields — read-only once derived from the generated page */}
          {fieldsReadOnly ? (
            <div className="flex flex-col gap-2">
              <Label>Form fields</Label>
              <div className="overflow-hidden rounded-lg border">
                {value.fields.map((field, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                      {field.label || field.key}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {field.key}
                    </span>
                    <span className="w-20 text-xs text-muted-foreground">
                      {fieldTypeLabels[field.type]}
                    </span>
                    <span className="w-16 text-right text-xs text-muted-foreground">
                      {field.required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Fields are derived from the form on the page. To change them,
                edit the form section on the page (via the edit chat or a
                regeneration).
              </p>
            </div>
          ) : (
          <div className="flex flex-col gap-2">
            <Label>Form fields</Label>
            <div className="flex flex-col gap-2">
              {value.fields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2"
                >
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Move up"
                      onClick={() => moveField(index, -1)}
                      disabled={index === 0}
                      className="text-muted-foreground/60 hover:text-foreground disabled:invisible"
                    >
                      <ChevronUpIcon className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      onClick={() => moveField(index, 1)}
                      disabled={index === value.fields.length - 1}
                      className="text-muted-foreground/60 hover:text-foreground disabled:invisible"
                    >
                      <ChevronDownIcon className="size-3.5" />
                    </button>
                  </div>
                  <Input
                    value={field.label}
                    onChange={(e) => patchField(index, { label: e.target.value })}
                    placeholder="Label"
                    maxLength={80}
                    className="h-8 flex-1"
                  />
                  <Input
                    value={field.key}
                    onChange={(e) =>
                      patchField(index, {
                        key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                      })
                    }
                    placeholder="key"
                    maxLength={40}
                    className="h-8 w-32 font-mono text-xs"
                  />
                  <Select
                    value={field.type}
                    onValueChange={(type) =>
                      patchField(index, { type: type as LeadFormFieldType })
                    }
                  >
                    <SelectTrigger className="h-8 w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(fieldTypeLabels) as LeadFormFieldType[]).map(
                        (type) => (
                          <SelectItem key={type} value={type}>
                            {fieldTypeLabels[type]}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      checked={field.required}
                      onCheckedChange={(required) => patchField(index, { required })}
                      aria-label="Required"
                    />
                    <span className="text-xs text-muted-foreground">Req.</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove field"
                    className="size-7"
                    onClick={() => removeField(index)}
                  >
                    <Trash2Icon className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={addField}
                disabled={value.fields.length >= MAX_FIELDS}
              >
                <PlusIcon className="size-4" />
                Add field
              </Button>
            </div>
          </div>
          )}

          {/* Success message */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="lead-form-success">Success message</Label>
            <Textarea
              id="lead-form-success"
              value={value.successMessage}
              onChange={(e) => patch({ successMessage: e.target.value })}
              maxLength={500}
              className="min-h-16"
            />
          </div>
        </>
      )}
    </div>
  );
}
